'use client';

import { useState, useEffect, useRef, useMemo } from 'react';
import { getTrackingMetadata } from '../utils/getTrackingMetadata';

export interface SourceDocument {
  metadata: {
    title?: string;
    source?: string;
    [key: string]: any;
  };
  pageContent: string;
}

export interface AISearchResponse {
  text: string;
  chatMessageId: string;
  chatId?: string;
  sourceDocuments?: SourceDocument[];
}

export interface UseAISearchOptions {
  query: string;
  chatflowid: string;
  apiHost: string;
  userEmail?: string;
  trackingData?: any;
  authType?: string;
  enabled: boolean;
}

export interface UseAISearchResult {
  data: AISearchResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export function useAISearch({
  query,
  chatflowid,
  apiHost,
  userEmail,
  trackingData,
  authType,
  enabled
}: UseAISearchOptions): UseAISearchResult {
  const [data, setData] = useState<AISearchResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isInitialLoadRef = useRef(true);
  // Track the last query to prevent re-fetching when query hasn't actually changed
  const lastQueryRef = useRef<string>('');
  // Track if we've already fetched for this query+enabled combo
  const lastEnabledRef = useRef<boolean>(false);

  // Extract stable primitive values from trackingData to avoid unnecessary re-fetches
  const user_id = trackingData?.user?.id || trackingData?.userId;
  const client_id = trackingData?.client_id;

  // Memoize tracking metadata to avoid recreating it on every render
  // Only recreate when the actual tracking values change, not when the object reference changes
  const trackingMetadata = useMemo(() => {
    return getTrackingMetadata({
      trackingData: {
        user: trackingData?.user?.id ? { id: trackingData.user.id } : undefined,
        userId: trackingData?.userId,
        client_id: trackingData?.client_id
      },
      authType,
      source: 'search_results'
    });
  }, [user_id, client_id, authType]);

  // Store latest trackingMetadata in a ref so we always use the latest value
  // without triggering re-fetches when tracking data changes
  const trackingMetadataRef = useRef(trackingMetadata);
  useEffect(() => {
    trackingMetadataRef.current = trackingMetadata;
  }, [trackingMetadata]);

  useEffect(() => {
    // Only re-fetch if the query actually changed OR enabled just became true
    // This prevents unnecessary reloads when trackingData or other state updates
    const trimmedQuery = query.trim();
    const enabledJustBecameTrue = enabled && !lastEnabledRef.current;

    // Update enabled ref
    lastEnabledRef.current = enabled;

    if (trimmedQuery === lastQueryRef.current && !enabledJustBecameTrue) {
      return;
    }

    // Update the last query ref
    lastQueryRef.current = trimmedQuery;

    // Reset state when query changes
    setData(null);
    setError(null);

    // Don't fetch if disabled or no query
    if (!enabled || !trimmedQuery || !chatflowid || !apiHost) {
      setIsLoading(false);
      return;
    }

    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const fetchAIResponse = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${apiHost}/api/v1/prediction/${chatflowid}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            question: trimmedQuery,
            streaming: false,
            ...(userEmail && {
              overrideConfig: {
                promptValues: {
                  email: userEmail
                }
              }
            }),
            trackingMetadata: trackingMetadataRef.current
          }),
          signal: abortController.signal
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();

        // Validate response has required fields
        if (!result.text) {
          throw new Error('Invalid API response: missing text field');
        }

        setData({
          text: result.text,
          chatMessageId: result.chatMessageId || result.chatId,
          chatId: result.chatId,
          sourceDocuments: result.sourceDocuments
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // Request was aborted, don't set error
          return;
        }
        console.error('AI Search error:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    // Skip debounce on initial load, debounce subsequent queries
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      fetchAIResponse();
      return () => {
        abortController.abort();
      };
    }

    // Debounce the API call to avoid rapid requests while typing
    const timeoutId = setTimeout(fetchAIResponse, 800);

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
    // Only depend on query and essential config - NOT tracking data
    // trackingMetadataRef always contains the latest value without triggering re-fetches
    // This prevents re-fetching when trackingData updates but query stays the same
  }, [query, chatflowid, apiHost, userEmail, enabled]);

  return { data, isLoading, error };
}

export default useAISearch;
